"use client"

import { useState, useTransition, useEffect } from "react"
import { Modal, Stack, Text, NumberInput, Group, Button, Badge, Alert } from "@forestsync/ui"
import { notify } from "@forestsync/ui"
import { useTranslations } from "next-intl"
import { modifyTradeOrder } from "@/actions/trading"
import { TRADE_ORDER_SIDE, TRADE_ORDER_TYPE } from "@forestsync/db"
import type { UserOpenOrder } from "@/lib/queries"

interface ModifyOrderModalProps {
  order: UserOpenOrder | null
  onClose: () => void
}

export function ModifyOrderModal({ order, onClose }: ModifyOrderModalProps) {
  const t = useTranslations("trading")
  const [price, setPrice] = useState<number | string>("")
  const [volume, setVolume] = useState<number | string>("")
  const [pending, startTransition] = useTransition()
  const [blockError, setBlockError] = useState<string | null>(null)

  useEffect(() => {
    if (order) {
      setPrice(order.price ?? "")
      setVolume(order.volume - order.filledVolume)
      setBlockError(null)
    }
  }, [order])

  function handleSubmit() {
    if (!order) return
    const isMarket = order.orderType === TRADE_ORDER_TYPE.MARKET
    const newPrice = isMarket ? undefined : Number(price)
    const newVolume = Number(volume)
    if (!isMarket && (!newPrice || newPrice <= 0)) return
    if (!newVolume || newVolume <= 0) return

    startTransition(async () => {
      const result = await modifyTradeOrder(order.id, { price: newPrice, volume: newVolume })
      if ("error" in result) {
        if (result.error?.includes("pre-authorized payment")) {
          setBlockError(t("modifyBlockedPaymentIntent"))
        } else {
          notify.error(result.error ?? t("orderError"))
        }
        return
      }
      notify.success(t("orderModified"))
      onClose()
    })
  }

  return (
    <Modal
      opened={order !== null}
      onClose={onClose}
      title={t("modifyOrder")}
      size="sm"
      centered
    >
      <Stack gap="md">
        <Group gap="xs">
          <Badge
            color={order?.side === TRADE_ORDER_SIDE.BID ? "green" : "red"}
            variant="light"
          >
            {order?.side === TRADE_ORDER_SIDE.BID ? t("buy") : t("sell")}
          </Badge>
          <Text size="sm" c="dimmed">
            {order?.creditType} · {order?.vintage}
          </Text>
        </Group>
        <Text size="xs" c="dimmed">{t("modifyOrderConfirm")}</Text>
        {blockError && (
          <Alert color="orange" variant="light" p="xs">
            <Text size="xs">{blockError}</Text>
          </Alert>
        )}
        {order?.orderType !== TRADE_ORDER_TYPE.MARKET && (
          <NumberInput
            label={t("newPrice")}
            value={price}
            onChange={setPrice}
            min={0.01}
            step={0.01}
            decimalScale={2}
          />
        )}
        <NumberInput
          label={t("newQuantity")}
          value={volume}
          onChange={setVolume}
          min={0.01}
          step={1}
          decimalScale={2}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={pending}>
            {t("no")}
          </Button>
          <Button loading={pending} onClick={handleSubmit}>
            {t("amendOrder")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
